// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/campaigns.test.tsx
import { type AskPunttProjectListItem } from "@mg/schemas/src/christo/askPunttProject";
import {
  CircleNotch,
  FolderSimplePlus,
  MagnifyingGlass,
  Plus,
  Trash,
  User,
} from "@phosphor-icons/react";
import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  Flex,
  Popover,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { campaignRoute, campaignSearch } from "./campaignRoute";
import {
  ListCardGridFooter,
  ListCardRowMembers,
  ListCardRowTimestamps,
} from "./components/ListCardGridFooter";
import { ListItemDeleteButton } from "./components/ListItemDeleteButton";
import { ListItemRenameButton } from "./components/ListItemRenameButton";
import { UnreadDot } from "./components/UnreadDot";
import { useInlineRename } from "./components/useInlineRename";
import { launcherSearch, type LauncherSearch } from "./launcherSearch";
import { askPunttProjectsRoute } from "./projectsRoute";
import { useStartNewProjectChat } from "./useStartNewProjectChat";
import {
  formatListCardAbsoluteDate,
  LIST_CARD_GRID_HEIGHT_CLASS,
  LIST_CARD_GRID_OPEN_BTN_CLASS,
  LIST_CARD_TITLE_RENAME_DELETE_RESERVE_CLASS,
} from "./utils/listCardTimestamps";
import { projectMemberLabel } from "./utils/projectListMembers";

import { interactiveSurfaceClasses } from "../../components/InteractiveSurface";
import { PageHeader } from "../../components/PageHeader";
import { ToolbarViewToggle } from "../../components/toolbar/ToolbarViewToggle";
import {
  TOOLBAR_CONTROL_SIZE,
  TOOLBAR_ICON_SIZE,
} from "../../components/toolbarControls";
import { useAppSelector } from "../../utils/hooks";
import { ASK_PUNTT_UNREAD_LIST_POLL_MS } from "../../utils/queries/askPuntt";
import {
  useAskPunttProjectMemberOptions,
  useAskPunttProjects,
  useDeleteAskPunttProject,
  useRenameAskPunttProject,
} from "../../utils/queries/askPunttProjects";

const MAX_ASK_PUNTT_PROJECT_NAME_LENGTH = 200;

/** Hover lift lives on the shell so owner actions move with the card. */
const PROJECT_CARD_OPEN_BTN = `${LIST_CARD_GRID_OPEN_BTN_CLASS} ${LIST_CARD_GRID_HEIGHT_CLASS}`;
const PROJECT_LIST_ROW_OPEN_BTN =
  "flex w-full cursor-pointer items-center gap-4 rounded-[inherit] border-0 bg-transparent p-4 text-left shadow-none";

const VIEW_STORAGE_KEY = "askpuntt-projects-view";

type ProjectsView = "grid" | "list";

function getStoredView(): ProjectsView {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "list" || stored === "grid") return stored;
  } catch {
    // ignore
  }
  return "grid";
}

export function formatProjectDate(iso: string | Date | undefined): string {
  return formatListCardAbsoluteDate(iso);
}

function canDeleteProject(
  project: AskPunttProjectListItem,
  currentUserId: string | undefined,
): boolean {
  // Only the project owner may rename/delete. Match the sidebar Recents gate
  // (role === "owner" OR ownerId === currentUserId). Unresolved auth is
  // treated as not-owner so we never flash controls on someone else's project.
  return (
    project.role === "owner" ||
    (currentUserId != null &&
      project.ownerId != null &&
      project.ownerId === currentUserId)
  );
}

function ProjectRenameInput({
  projectId,
  draft,
  setDraft,
  commit,
  cancel,
}: {
  projectId: string;
  draft: string;
  setDraft: (next: string) => void;
  commit: () => void;
  cancel: () => void;
}) {
  const { t } = useTranslation("ui");
  return (
    <input
      autoFocus
      value={draft}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancel();
        }
      }}
      maxLength={MAX_ASK_PUNTT_PROJECT_NAME_LENGTH}
      aria-label={t("projectName")}
      data-testid={`campaign-row-rename-input-${projectId}`}
      className="w-full min-w-0 rounded border border-puntt-accent-9 bg-base-white px-1.5 py-0.5 text-base font-bold text-puntt-neutral-gray-12 outline-none focus:ring-1 focus:ring-puntt-accent-8"
    />
  );
}

function ProjectOwnerActions({
  project,
  isRenaming,
  isPending,
  onStartRename,
  onRequestDelete,
}: {
  project: AskPunttProjectListItem;
  isRenaming: boolean;
  isPending: boolean;
  onStartRename: () => void;
  onRequestDelete: (projectId: string) => void;
}) {
  if (isRenaming) return null;
  return (
    <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
      <ListItemRenameButton
        ariaLabel={`Rename project ${project.name}`}
        data-testid={`campaign-row-rename-${project._id}`}
        isPending={isPending}
        onClick={onStartRename}
      />
      <ListItemDeleteButton
        ariaLabel={`Delete project ${project.name}`}
        data-testid={`campaign-row-delete-${project._id}`}
        onClick={() => onRequestDelete(project._id)}
      />
    </div>
  );
}

function ProjectCard({
  project,
  currentUserId,
  onOpen,
  onRequestDelete,
}: {
  project: AskPunttProjectListItem;
  currentUserId: string | undefined;
  onOpen: (projectId: string) => void;
  onRequestDelete: (projectId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const members = project.members ?? [];
  const canDelete = canDeleteProject(project, currentUserId);
  const renameMutation = useRenameAskPunttProject(project._id);
  const { isRenaming, draft, setDraft, startRename, commit, cancel } =
    useInlineRename({
      value: project.name,
      onCommit: (next) => renameMutation.mutate(next),
    });

  return (
    <div
      className="group relative"
      data-testid={`campaign-row-${project._id}`}
      data-project-id={project._id}
    >
      {isRenaming ? (
        <div
          className={`${interactiveSurfaceClasses({
            interactive: false,
          })} ${LIST_CARD_GRID_HEIGHT_CLASS} flex w-full flex-col justify-between border-puntt-accent-7 p-5 pb-4 text-left`}
        >
          <div className="min-w-0">
            <ProjectRenameInput
              projectId={project._id}
              draft={draft}
              setDraft={setDraft}
              commit={commit}
              cancel={cancel}
            />
            {project.description ? (
              <Text
                size="2"
                className="mt-2 line-clamp-2 text-puntt-neutral-gray-10"
              >
                {project.description}
              </Text>
            ) : null}
          </div>
          <ListCardGridFooter
            idPrefix="campaign-row"
            entityId={project._id}
            updatedAt={project.updatedAt}
            members={members}
            memberLabel={projectMemberLabel}
          />
        </div>
      ) : (
        <div
          className={`${interactiveSurfaceClasses({
            interactive: true,
          })} relative w-full`}
          data-testid={`campaign-row-surface-${project._id}`}
        >
          <button
            type="button"
            onClick={() => onOpen(project._id)}
            aria-label={t("campaigns.openProject", { name: project.name })}
            data-testid={`campaign-row-open-${project._id}`}
            className={PROJECT_CARD_OPEN_BTN}
          >
            <div className="min-w-0">
              <Text
                as="div"
                size="4"
                weight="bold"
                className={`flex items-center gap-2 truncate text-puntt-neutral-gray-12${
                  canDelete
                    ? ` ${LIST_CARD_TITLE_RENAME_DELETE_RESERVE_CLASS}`
                    : ""
                }`}
              >
                <span className="truncate">{project.name}</span>
                {project.hasUnread ? (
                  <UnreadDot
                    testId={`campaign-row-unread-${project._id}`}
                    ariaLabel={t("askPuntt.campaigns.unread")}
                  />
                ) : null}
              </Text>
              {project.description ? (
                <Text
                  size="2"
                  className="mt-2 line-clamp-2 text-puntt-neutral-gray-10"
                >
                  {project.description}
                </Text>
              ) : null}
            </div>
            <ListCardGridFooter
              idPrefix="campaign-row"
              entityId={project._id}
              updatedAt={project.updatedAt}
              members={members}
              memberLabel={projectMemberLabel}
              hideOnGroupHover={canDelete}
            />
          </button>
          {canDelete ? (
            <ProjectOwnerActions
              project={project}
              isRenaming={isRenaming}
              isPending={renameMutation.isPending}
              onStartRename={startRename}
              onRequestDelete={onRequestDelete}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function ProjectListRow({
  project,
  currentUserId,
  onOpen,
  onRequestDelete,
}: {
  project: AskPunttProjectListItem;
  currentUserId: string | undefined;
  onOpen: (projectId: string) => void;
  onRequestDelete: (projectId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const canDelete = canDeleteProject(project, currentUserId);
  const renameMutation = useRenameAskPunttProject(project._id);
  const { isRenaming, draft, setDraft, startRename, commit, cancel } =
    useInlineRename({
      value: project.name,
      onCommit: (next) => renameMutation.mutate(next),
    });

  return (
    <div
      className="group relative"
      data-testid={`campaign-row-${project._id}`}
      data-project-id={project._id}
    >
      {isRenaming ? (
        <div
          className={`${interactiveSurfaceClasses({
            interactive: false,
          })} flex w-full items-center gap-4 border-puntt-accent-7 p-4 pr-12 text-left`}
        >
          <div className="min-w-0 flex-1">
            <ProjectRenameInput
              projectId={project._id}
              draft={draft}
              setDraft={setDraft}
              commit={commit}
              cancel={cancel}
            />
            {project.description ? (
              <Text
                size="1"
                className="mt-1 block truncate text-puntt-neutral-gray-11"
              >
                {project.description}
              </Text>
            ) : null}
          </div>
        </div>
      ) : (
        <div
          className={`${interactiveSurfaceClasses({
            interactive: true,
          })} relative w-full`}
          data-testid={`campaign-row-surface-${project._id}`}
        >
          <button
            type="button"
            onClick={() => onOpen(project._id)}
            aria-label={t("campaigns.openProject", { name: project.name })}
            data-testid={`campaign-row-open-${project._id}`}
            className={PROJECT_LIST_ROW_OPEN_BTN}
          >
            <div className="min-w-0 flex-1">
              <Text
                size="3"
                weight="medium"
                className={`flex items-center gap-2 truncate text-puntt-neutral-gray-12${
                  canDelete
                    ? ` ${LIST_CARD_TITLE_RENAME_DELETE_RESERVE_CLASS}`
                    : ""
                }`}
              >
                <span className="truncate">{project.name}</span>
                {project.hasUnread ? (
                  <UnreadDot
                    testId={`campaign-row-unread-${project._id}`}
                    ariaLabel={t("askPuntt.campaigns.unread")}
                  />
                ) : null}
              </Text>
              {project.description ? (
                <Text
                  size="1"
                  className="block truncate text-puntt-neutral-gray-11"
                >
                  {project.description}
                </Text>
              ) : null}
            </div>
            <Flex align="center" gap="3" className="ml-auto shrink-0">
              <ListCardRowTimestamps
                idPrefix="campaign-row"
                entityId={project._id}
                updatedAt={project.updatedAt}
              />
              <ListCardRowMembers
                idPrefix="campaign-row"
                entityId={project._id}
                members={project.members ?? []}
                memberLabel={projectMemberLabel}
              />
            </Flex>
          </button>
          {canDelete ? (
            <ProjectOwnerActions
              project={project}
              isRenaming={isRenaming}
              isPending={renameMutation.isPending}
              onStartRename={startRename}
              onRequestDelete={onRequestDelete}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function AskPunttCampaigns() {
  const { t } = useTranslation("ui");
  const { t: tComponents } = useTranslation("components");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const urlSearch = useSearch({ strict: false }) as LauncherSearch;
  const { startNewProjectChat, isPending: isStartingProject } =
    useStartNewProjectChat("ask_puntt_campaigns");
  const [search, setSearch] = useState(urlSearch.q ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch.q ?? "");
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const view: ProjectsView = urlSearch.view ?? getStoredView();
  const [projectIdToDelete, setProjectIdToDelete] = useState<string | null>(
    null,
  );
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const deleteProjectMutation = useDeleteAskPunttProject();

  const syncLauncherSearch = useCallback(
    (patch: Partial<LauncherSearch>) => {
      navigate({
        to: askPunttProjectsRoute.to,
        search: launcherSearch({ ...urlSearch, ...patch }),
        replace: true,
      });
    },
    [navigate, urlSearch],
  );

  useEffect(() => {
    setSearch(urlSearch.q ?? "");
    setDebouncedSearch(urlSearch.q ?? "");
  }, [urlSearch.q]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const q = debouncedSearch.trim() || undefined;
    if (q === urlSearch.q) return;
    syncLauncherSearch({ q });
  }, [debouncedSearch, urlSearch.q, syncLauncherSearch]);

  const { data, isLoading, isError, isFetching } = useAskPunttProjects({
    scope: "all",
    search: debouncedSearch,
    participantIds,
    refetchInterval: ASK_PUNTT_UNREAD_LIST_POLL_MS,
  });
  const { data: uniqueMembers = [] } = useAskPunttProjectMemberOptions();

  const projects = data?.projects ?? [];
  const projectToDelete = projects.find((p) => p._id === projectIdToDelete);
  const isSearchPending = search.trim() !== debouncedSearch.trim();

  const selectedParticipants = uniqueMembers.filter((m) =>
    participantIds.includes(m._id),
  );

  const participantFilterLabel = (() => {
    if (selectedParticipants.length === 0) return "Participants";
    if (selectedParticipants.length === 1) {
      return projectMemberLabel(selectedParticipants[0]);
    }
    return `${selectedParticipants.length} participants`;
  })();

  const toggleParticipant = (memberId: string, checked: boolean) => {
    setParticipantIds((prev) => {
      if (checked) {
        return prev.includes(memberId) ? prev : [...prev, memberId];
      }
      return prev.filter((id) => id !== memberId);
    });
  };

  const handleViewChange = (next: ProjectsView) => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    syncLauncherSearch({ view: next });
  };

  const openProject = (projectId: string) => {
    navigate({
      to: campaignRoute.to,
      params: { projectId },
      search: campaignSearch(),
    });
  };

  const handleNewProject = () => {
    void startNewProjectChat("campaigns_launcher");
  };

  const handleDeleteProject = (projectId: string) => {
    deleteProjectMutation.mutate(projectId, {
      onSuccess: () => setProjectIdToDelete(null),
    });
  };

  const hasFilters =
    debouncedSearch.trim().length > 0 || participantIds.length > 0;
  const listLoading = isLoading || isFetching || isSearchPending;
  const showNoResults =
    !listLoading && !isError && hasFilters && projects.length === 0;
  const showEmpty =
    !listLoading && !isError && !hasFilters && projects.length === 0;

  return (
    <div
      className="ask-puntt-surface flex h-full flex-col overflow-y-auto"
      data-testid="ask-puntt-campaigns"
    >
      <PageHeader className="justify-between gap-4">
        <Text size="5" weight="bold" className="text-puntt-accent-9">
          {t("projects")}
        </Text>
        <Flex align="center" gap="2" wrap="wrap">
          <TextField.Root
            data-testid="campaigns-search"
            placeholder={t("searchProjects")}
            size={TOOLBAR_CONTROL_SIZE}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t("searchProjects")}
            className="min-w-[200px]"
          >
            <TextField.Slot>
              <MagnifyingGlass size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
            </TextField.Slot>
          </TextField.Root>

          <Popover.Root
            open={participantsOpen}
            onOpenChange={setParticipantsOpen}
          >
            <Popover.Trigger>
              <Button
                type="button"
                variant="surface"
                color="gray"
                size={TOOLBAR_CONTROL_SIZE}
                data-testid="campaigns-participants-filter"
                aria-label={
                  participantIds.length > 0
                    ? `Filter by participants, ${participantIds.length} selected`
                    : "Filter by participants"
                }
                className="cursor-pointer"
              >
                <User size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
                <Text
                  size="2"
                  weight="regular"
                  className="text-puntt-neutral-gray-11"
                >
                  {participantFilterLabel}
                </Text>
              </Button>
            </Popover.Trigger>
            <Popover.Content
              align="end"
              className="min-w-[220px] p-2"
              data-testid="campaigns-participants-popover"
            >
              {uniqueMembers.length === 0 ? (
                <Text size="2" className="px-2 py-1 text-puntt-neutral-gray-9">
                  {t("noParticipantsYet")}
                </Text>
              ) : (
                <div className="flex flex-col gap-1">
                  {uniqueMembers.map((member) => {
                    const checked = participantIds.includes(member._id);
                    const optionId = `campaigns-participant-${member._id}`;
                    return (
                      <label
                        key={member._id}
                        htmlFor={optionId}
                        data-testid={`campaigns-participant-option-${member._id}`}
                        className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm text-puntt-neutral-gray-12 transition-colors hover:bg-puntt-neutral-gray-3"
                      >
                        <Checkbox
                          id={optionId}
                          checked={checked}
                          onCheckedChange={(value) =>
                            toggleParticipant(member._id, value === true)
                          }
                          aria-label={projectMemberLabel(member)}
                        />
                        {projectMemberLabel(member)}
                      </label>
                    );
                  })}
                </div>
              )}
              {participantIds.length > 0 ? (
                <button
                  type="button"
                  data-testid="campaigns-participant-clear"
                  aria-label={t("clearParticipantFilters")}
                  className="mt-2 w-full cursor-pointer rounded-md border-t border-puntt-neutral-gray-4 px-3 py-2 text-left text-sm text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3"
                  onClick={() => setParticipantIds([])}
                >
                  {t("clearFilters")}
                </button>
              ) : null}
            </Popover.Content>
          </Popover.Root>

          <ToolbarViewToggle
            value={view}
            onChange={handleViewChange}
            groupLabel="Project view"
            data-testid="campaigns-view-toggle"
            testIds={{
              grid: "campaigns-view-grid",
              list: "campaigns-view-list",
            }}
          />

          <Button
            data-testid="campaigns-new-button"
            size={TOOLBAR_CONTROL_SIZE}
            disabled={isStartingProject}
            onClick={handleNewProject}
            className="cursor-pointer"
          >
            {isStartingProject ? (
              <CircleNotch
                size={14}
                weight="bold"
                className="animate-spin"
                aria-hidden="true"
              />
            ) : (
              <Plus size={14} weight="bold" aria-hidden="true" />
            )}
            {tComponents("askPuntt.campaigns.newProject")}
          </Button>
        </Flex>
      </PageHeader>

      <div className="mx-auto w-full max-w-[960px] px-6 py-8">
        {listLoading ? (
          <div
            className={
              view === "grid" ? "grid grid-cols-2 gap-4" : "flex flex-col gap-2"
            }
            data-testid="campaigns-skeleton"
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className={
                  view === "grid"
                    ? "h-40 animate-pulse rounded-2xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                    : "h-20 animate-pulse rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                }
              />
            ))}
          </div>
        ) : isError ? (
          <Callout.Root color="red" size="1" data-testid="campaigns-error">
            <Callout.Text>{t("failedLoadProjects")}</Callout.Text>
          </Callout.Root>
        ) : showEmpty ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="4"
            className="mt-16"
            data-testid="campaigns-empty"
          >
            <FolderSimplePlus
              size={40}
              weight="duotone"
              className="text-puntt-neutral-gray-8"
              aria-hidden="true"
            />
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              {t("noProjectsYet")}
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              {t("campaigns.emptyDescription")}
            </Text>
            <Button
              data-testid="campaigns-empty-new-button"
              disabled={isStartingProject}
              onClick={handleNewProject}
              className="cursor-pointer"
            >
              {isStartingProject ? (
                <CircleNotch
                  size={14}
                  weight="bold"
                  className="animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <Plus size={14} weight="bold" aria-hidden="true" />
              )}
              {tComponents("askPuntt.campaigns.empty.button")}
            </Button>
          </Flex>
        ) : showNoResults ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="3"
            className="mt-16"
            data-testid="campaigns-no-results"
          >
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              {t("noProjectsMatch")}
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              {t("tryAdjustingSearchOrFilter")}
            </Text>
            <Button
              variant="soft"
              color="gray"
              data-testid="campaigns-clear-filters"
              aria-label={t("clearAllFilters")}
              className="cursor-pointer"
              onClick={() => {
                setSearch("");
                setParticipantIds([]);
              }}
            >
              {t("clearFilters")}
            </Button>
          </Flex>
        ) : view === "grid" ? (
          <div className="grid grid-cols-2 gap-4" data-testid="campaigns-grid">
            {projects.map((p) => (
              <ProjectCard
                key={p._id}
                project={p}
                currentUserId={currentUserId}
                onOpen={openProject}
                onRequestDelete={setProjectIdToDelete}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="campaigns-list">
            {projects.map((p) => (
              <ProjectListRow
                key={p._id}
                project={p}
                currentUserId={currentUserId}
                onOpen={openProject}
                onRequestDelete={setProjectIdToDelete}
              />
            ))}
          </div>
        )}
      </div>

      <Dialog.Root
        open={projectIdToDelete != null}
        onOpenChange={(open) => {
          if (!open) setProjectIdToDelete(null);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>{t("deleteProjectQ")}</Dialog.Title>
          <Dialog.Description>
            {/* One interpolated sentence, not three fragments: verb-final and
                RTL languages cannot reorder fixed-order fragments, and the
                trailing clause has no subject to translate against. These are
                the same keys AppSidebar renders for this dialog. */}
            {projectToDelete
              ? tCommon("nav.areYouSureDeleteProject", {
                  name: projectToDelete.name,
                })
              : tCommon("nav.areYouSureDeleteProjectFallback")}
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setProjectIdToDelete(null)}
              data-testid="campaigns-delete-cancel"
            >
              {t("cancel")}
            </Button>
            <Button
              color="red"
              onClick={() =>
                projectIdToDelete != null &&
                handleDeleteProject(projectIdToDelete)
              }
              disabled={deleteProjectMutation.isPending}
              loading={deleteProjectMutation.isPending}
              data-testid="campaigns-delete-confirm"
            >
              <Trash aria-hidden="true" />
              {t("delete")}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
